import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Flex,
  Input,
  Layout,
  Space,
  Table,
  TableProps
} from "antd";
import { ColumnsType } from "antd/es/table";
import { get, toNumber } from "lodash";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionContent from "src/components/common/ActionContent";
import ModalDeleteConfirm from "src/components/common/ModalDeleteConfirm";
import { apiCaller, cancelListToken } from "src/util/apiCaller";
import {
  getLocalStorage,
  reDataForTable,
  setLocalStorage
} from "src/util/Commom";
import { MessageStatusError } from "src/util/MessageStatus";

interface Data {
  key: React.Key;
  tenVaiTro: string;
  maVaiTro: string;
}

const VaiTro = ({ status }: any) => {
  const { isActive } = status;
  const [isLoadTable, setIsLoadTable] = useState<boolean>(false);
  const [datatable, setDatatable] = useState([]);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [isSearchClick, setIsSearchClick] = useState(false);
  const pageSize = 10;
  const navigate = useNavigate();
  useEffect(() => {
    if (isActive) {
      const state = getLocalStorage("dataFilter") || undefined;
      if (state) {
        setKeyword(state.keyword);
        setActivePage(state.activePage);
        getListData(state.activePage, state.keyword);
      } else {
        getListData(activePage, keyword);
      }
    } else {
      navigate("/error/401");
    }
    return () => {
      cancelListToken(["listVaiTro"]);
    };
  }, []);

  /**
   * getListData: gọi danh sách vai trò
   * @param {int} activePage
   * @param {string} keyword
   * @memberof VaiTro
   */
  const getListData = async (activePage: number, keyword: string) => {
    setIsLoadTable(true);
    const configAPI = {
      url: `api/vaitros?keyword=${keyword}&page=${activePage}&size=${pageSize}`,
      method: "GET",
      value: null,
      apiType: "LIST",
      getName: "listVaiTro"
    } as any;
    const response = await apiCaller(configAPI);
    try {
      if (
        response?.data &&
        (response.status === 200 || response.status === 201)
      ) {
        const listVaiTro = get(response.data, "content", []).map(
          (item: { [x: string]: any; children: any }) => {
            const { children, ...rest } = item;
            return rest;
          }
        );
        const data = reDataForTable(listVaiTro, activePage, pageSize);
        setDatatable(data as any);
        setTotal(get(listVaiTro, "totalElements", 0));
        setIsLoadTable(false);
      } else {
        MessageStatusError(toNumber(response?.status));
        setIsLoadTable(false);
      }
    } finally {
      setIsLoadTable(false);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: "STT",
      dataIndex: "key",
      key: "key",
      width: "5%",
      align: "center",
      render: (text, record, index) => <span>{index + 1}</span>
    },
    { title: "Tên vai trò", dataIndex: "tenVaiTro", key: "tenVaiTro" },
    { title: "Mã vai trò", dataIndex: "maVaiTro", key: "maVaiTro" },
    {
      title: "Hành động",
      dataIndex: "",
      key: "x",
      align: "center",
      width: "10%",
      render: (item) => actionContent(item)
    }
  ];

  /**
   * actionContent: hành động trên bảng
   * @param {object} item
   * @memberof VaiTro
   */
  const actionContent = (item: object) => {
    return (
      <ActionContent
        onDeleteItem={() => deleteItemFunc(item)}
        onEditItem={() => handleRedirect("edit", item)}
      />
    );
  };

  /**
   * deleteItemFunc: Xoá item theo item
   * @param {object} item
   * @memberof VaiTro
   */
  const deleteItemFunc = (item: object) => {
    ModalDeleteConfirm(deleteItemAction, item);
  };

  /**
   * deleteItemAction: Xóa item
   * @param {object} item
   * @memberof VaiTro
   */
  const deleteItemAction = async (item: any) => {
    const config = {
      url: `api/vaitros/${item.id}`,
      apiType: "DELETE",
      value: null,
      method: "DELETE",
      getName: "deleteListQuyen"
    };
    const response = await apiCaller(config);
    try {
      if (response?.status === 200 || response?.status === 201) {
        const dataPage = total - (activePage - 1) * pageSize;
        const page = dataPage <= 1 && total !== 1 ? activePage - 1 : activePage;
        getListData(page, keyword);
        setIsLoadTable(true);
      } else {
        MessageStatusError(toNumber(response?.status));
        setIsLoadTable(false);
      }
    } catch (error) {
      setIsLoadTable(false);
      // Xử lý lỗi khi lưu dữ liệu không thành công
    }
  };

  /**
   * handleRedirect: Chuyển trang thêm mới và chỉnh sửa
   * @param {string} type
   * @param {object} item
   * @memberof VaiTro
   */
  const handleRedirect = (type = "", item: any) => {
    let pathname = "/quan-tri/vai-tro";
    switch (type) {
      case "new":
        pathname += "/them-moi/";
        break;
      case "edit":
        pathname += `/${item.id}/chinh-sua/`;
        break;
      default:
        break;
    }
    navigate(pathname, { state: { itemData: item } });
    if (isSearchClick) {
      const content = {
        keyword,
        activePage,
        pageSize,
        pathname: "quan-tri/vai-tro"
      };
      setLocalStorage("dataFilter", content);
    }
  };

  /**
   * handleTableChange: Fetch dữ liệu dựa theo thay đổi trang
   * @param {object} pagination
   * @memberof VaiTro
   */
  const handleTableChange: TableProps["onChange"] = (pagination) => {
    const activePage = pagination.current as number;
    getListData(activePage, keyword);
    setActivePage(activePage);
  };

  /**
   * onChangeText: Thay đổi keyword tìm kiếm
   * @param {object} e
   * @memberof VaiTro
   */
  const onChangeText = (e: any) => {
    const keyword = e.target.value;
    setKeyword(keyword);
  };

  /**
   * handleSubmit: Thực hiện hành động tìm kiếm
   * @memberof VaiTro
   */
  const handleSubmit = () => {
    setIsSearchClick(true);
    getListData(activePage, keyword);
  };

  return (
    <Layout>
      <Flex justify="space-between" align="center">
        <h3 style={{ color: "black", fontSize: 20 }}>Vai trò</h3>
        <Space>
          <Input
            className="heading-input-search"
            name="keyword"
            onPressEnter={handleSubmit}
            onChange={onChangeText}
            placeholder="Nhập từ khóa..."
            value={keyword}
            allowClear
            type="text"
          />
          <Button
            className="heading-button-search btn-success"
            icon={<SearchOutlined />}
            onClick={handleSubmit}
          />
          <Button
            className="heading-button-addnew btn"
            type="primary"
            onClick={() => handleRedirect("new", null)}
            icon={<PlusOutlined style={{ marginRight: 3 }} />}
          >
            Thêm mới
          </Button>
        </Space>
      </Flex>
      <Card>
        <Table
          rowKey={"maVaiTro"}
          bordered
          columns={columns}
          className="qti-table-wrapper gx-table-responsive"
          dataSource={datatable}
          rowClassName={(record, index) =>
            index % 2 === 0 ? "qti-table-row-background" : ""
          }
          size="middle"
          pagination={{
            current: activePage,
            pageSize,
            total,
            showTotal: (total) =>
              pageSize <= total
                ? `Hiển thị ${pageSize} trong tổng ${total}`
                : `Tổng ${total}`
          }}
          loading={isLoadTable}
          onChange={handleTableChange}
          scroll={{ x: 700 }}
        />
      </Card>
    </Layout>
  );
};
export default VaiTro;
